import {
  ProjectSectionIntakeEventType,
  ProjectFolderEventType,
  ProjectHotelEventType,
  ProjectBookingEventType,
  type ProjectContainerEvent,
  type ProjectFolderEvent,
  type ProjectContainerListEvent,
  type ProjectHotelRealtimeEvent,
  type ProjectAssessmentRealtimeEvent,
  type ProjectBookingRealtimeEvent
} from './projectContainerEventTypes';

const normalizeSectionIntakeEventType = (rawType: unknown): ProjectSectionIntakeEventType | undefined => {
  if (typeof rawType !== 'number') {
    return undefined;
  }

  return ProjectSectionIntakeEventType[rawType as ProjectSectionIntakeEventType] !== undefined
    ? (rawType as ProjectSectionIntakeEventType)
    : undefined;
};

const normalizeFolderEventType = (rawType: unknown): ProjectFolderEventType | undefined => {
  if (typeof rawType !== 'number') {
    return undefined;
  }

  return ProjectFolderEventType[rawType as ProjectFolderEventType] !== undefined
    ? (rawType as ProjectFolderEventType)
    : undefined;
};

const normalizeProjectHotelEventType = (rawType: unknown): ProjectHotelEventType | undefined => {
  if (typeof rawType !== 'number') {
    return undefined;
  }

  return ProjectHotelEventType[rawType as ProjectHotelEventType] !== undefined
    ? (rawType as ProjectHotelEventType)
    : undefined;
};

const normalizeProjectBookingEventType = (rawType: unknown): ProjectBookingEventType | undefined => {
  if (typeof rawType !== 'number') {
    return undefined;
  }

  return ProjectBookingEventType[rawType as ProjectBookingEventType] !== undefined
    ? (rawType as ProjectBookingEventType)
    : undefined;
};

const parseNumeric = (value: unknown): number | undefined => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const parsed = Number.parseInt(value, 10);
    return Number.isNaN(parsed) ? undefined : parsed;
  }
  return undefined;
};

export const normalizeContainerEvent = (args: unknown[]): ProjectContainerEvent | null => {
  if (!args.length) {
    return null;
  }

  const [first] = args;

  if (typeof first !== 'object' || first === null) {
    return null;
  }

  const raw = first as any;

  const containerIdNum = parseNumeric(raw.containerId ?? raw.ContainerId);
  const intakeIdNum = parseNumeric(raw.intakeId ?? raw.IntakeId);
  const fromFolderIdRaw = raw.fromFolderId ?? raw.FromFolderId;
  const toFolderIdRaw = raw.toFolderId ?? raw.ToFolderId;
  const typeRaw = raw.type ?? raw.Type;
  const data = raw.data ?? raw.Data;

  const fromFolderIdNum =
    fromFolderIdRaw === undefined || fromFolderIdRaw === null ? undefined : parseNumeric(fromFolderIdRaw);
  const toFolderIdNum = toFolderIdRaw === undefined || toFolderIdRaw === null ? undefined : parseNumeric(toFolderIdRaw);

  const type = normalizeSectionIntakeEventType(typeRaw);

  if (containerIdNum === undefined || intakeIdNum === undefined || type === undefined) {
    return null;
  }

  return {
    containerId: containerIdNum,
    intakeId: intakeIdNum,
    type,
    fromFolderId: fromFolderIdNum,
    toFolderId: toFolderIdNum,
    data
  };
};

export const normalizeFolderEvent = (args: unknown[]): ProjectFolderEvent | null => {
  if (!args.length) {
    return null;
  }

  const [first] = args;

  if (typeof first !== 'object' || first === null) {
    return null;
  }

  const raw = first as any;

  const containerIdNum = parseNumeric(raw.containerId ?? raw.ContainerId);
  const folderIdNum = parseNumeric(raw.folderId ?? raw.FolderId);
  const typeRaw = raw.type ?? raw.Type;
  const name: string | undefined = raw.name ?? raw.Name;

  const type = normalizeFolderEventType(typeRaw);

  if (containerIdNum === undefined || folderIdNum === undefined || type === undefined) {
    return null;
  }

  return {
    containerId: containerIdNum,
    folderId: folderIdNum,
    type,
    name
  };
};

export const normalizeProjectContainerListEvent = (dto: unknown): ProjectContainerListEvent | null => {
  if (!dto || typeof dto !== 'object') return null;
  const raw = dto as any;

  // Backend currently sends PascalCase properties, but we defensively
  // support both camelCase and PascalCase.
  const containerId: unknown = raw.containerId ?? raw.ContainerId;
  const companyId: unknown = raw.companyId ?? raw.CompanyId;

  if (typeof containerId !== 'number' || typeof companyId !== 'number') return null;

  // Type can be a string ("Created" | "Updated") or a numeric enum
  // value. Normalize both forms to the string union expected by the
  // rest of the realtime helpers.
  const typeRaw: unknown = raw.type ?? raw.Type;
  let type: 'Created' | 'Updated' | null = null;

  if (typeof typeRaw === 'string') {
    if (typeRaw === 'Created' || typeRaw === 'Updated') {
      type = typeRaw;
    }
  } else if (typeof typeRaw === 'number') {
    // C# enum ProjectContainerEventType: assume 0 = Created, 1 = Updated
    if (typeRaw === 0) type = 'Created';
    if (typeRaw === 1) type = 'Updated';
  }

  if (!type) return null;

  const name: string = String(raw.name ?? raw.Name ?? '');
  const assignedToUserId: string | null = raw.assignedToUserId ?? raw.AssignedToUserId ?? null;
  const additionalAssignedRaw = raw.additionalAssignedToUserIds ?? raw.AdditionalAssignedToUserIds;
  const additionalAssignedToUserIds = Array.isArray(additionalAssignedRaw)
    ? additionalAssignedRaw.map((id) => String(id)).filter(Boolean)
    : [];
  const stage: string = String(raw.stage ?? raw.Stage ?? raw.status ?? raw.Status ?? '');

  return {
    containerId,
    companyId,
    name,
    assignedToUserId,
    additionalAssignedToUserIds,
    stage,
    type
  };
};

export const normalizeProjectHotelEvent = (args: unknown[]): ProjectHotelRealtimeEvent | null => {
  if (!args.length) {
    return null;
  }

  const [first] = args;

  if (typeof first !== 'object' || first === null) {
    return null;
  }

  const raw = first as any;
  const containerId = parseNumeric(raw.containerId ?? raw.ContainerId);
  const type = normalizeProjectHotelEventType(raw.type ?? raw.Type);

  if (containerId === undefined || type === undefined) {
    return null;
  }

  return {
    containerId,
    type,
    projectHotelId: parseNumeric(raw.projectHotelId ?? raw.ProjectHotelId),
    hotelRateId: parseNumeric(raw.hotelRateId ?? raw.HotelRateId),
    projectIntakeId: parseNumeric(raw.projectIntakeId ?? raw.ProjectIntakeId)
  };
};

export const normalizeProjectBookingEvent = (args: unknown[]): ProjectBookingRealtimeEvent | null => {
  if (!args.length) {
    return null;
  }

  const [first] = args;

  if (typeof first !== 'object' || first === null) {
    return null;
  }

  const raw = first as any;
  const containerId = parseNumeric(raw.containerId ?? raw.ContainerId);
  const type = normalizeProjectBookingEventType(raw.type ?? raw.Type);
  const activityId = parseNumeric(raw.activityId ?? raw.ActivityId);

  if (containerId === undefined || type === undefined) {
    return null;
  }

  return {
    containerId,
    type,
    ...(activityId === undefined ? {} : { activityId }),
    projectBookingId: parseNumeric(raw.projectBookingId ?? raw.ProjectBookingId),
    projectHotelId: parseNumeric(raw.projectHotelId ?? raw.ProjectHotelId)
  };
};

export const normalizeProjectAssessmentEvent = (args: unknown[]): ProjectAssessmentRealtimeEvent | null => {
  if (!args.length) {
    return null;
  }

  const [first] = args;

  if (typeof first !== 'object' || first === null) {
    return null;
  }

  const raw = first as any;
  const containerId = parseNumeric(raw.containerId ?? raw.ContainerId);

  return containerId === undefined ? null : { containerId };
};
